import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useUpdateServiceCosting } from '~/hooks/apis/tours/useTourSuppliers';

import { TourService } from '~/types/tours/costPrice';
import {
  SERVICE_COST_PRICE_SCOPES,
  SERVICE_COST_PRICE_SCOPE_LABELS,
  SERVICE_COST_PRICE_TYPES,
  SERVICE_COST_PRICE_TYPE_LABELS,
  ServiceCostPriceScope,
  ServiceCostPriceType,
  costPriceScopeLabel,
  costPriceTypeLabel,
} from '~/types/tours/supplier';

interface Props {
  open: boolean;
  service: TourService;
  tourId: string;
  /** Cost prices are stored in the shape the costing sets, so changing it removes them. */
  hasCostPrices: boolean;
  onClose: () => void;
}

export default function ServiceCostingDialog({ open, service, tourId, hasCostPrices, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [costPriceType, setCostPriceType] = useState<ServiceCostPriceType>(service.costPriceType);
  const [costPriceScope, setCostPriceScope] = useState<ServiceCostPriceScope>(service.costPriceScope);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const updateCosting = useUpdateServiceCosting({ supplierId: service.supplierId, serviceId: service.id, tourId });

  const isChanged = costPriceType !== service.costPriceType || costPriceScope !== service.costPriceScope;
  const needsDiscard = isChanged && hasCostPrices;

  const handleSave = async () => {
    try {
      await updateCosting.mutateAsync({ costPriceType, costPriceScope, discardCostPrices: needsDiscard });
      enqueueSnackbar(
        `Costed as ${costPriceTypeLabel(costPriceType).toLowerCase()}, ${costPriceScopeLabel(costPriceScope).toLowerCase()}`,
        { variant: 'success' },
      );
      onClose();
    } catch (error) {
      enqueueSnackbar(`Could not change costing: ${error?.message ?? 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change costing</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            label="Cost price"
            value={costPriceType}
            onChange={(event) => setCostPriceType(event.target.value as ServiceCostPriceType)}
          >
            {SERVICE_COST_PRICE_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {SERVICE_COST_PRICE_TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Priced for"
            value={costPriceScope}
            onChange={(event) => setCostPriceScope(event.target.value as ServiceCostPriceScope)}
            helperText={
              costPriceScope === 'Departure'
                ? 'Each departure gets its own prices. Choose several departures to price them with one sheet.'
                : 'One set of prices per season, for every departure.'
            }
          >
            {SERVICE_COST_PRICE_SCOPES.map((scope) => (
              <MenuItem key={scope} value={scope}>
                {SERVICE_COST_PRICE_SCOPE_LABELS[scope]}
              </MenuItem>
            ))}
          </TextField>

          {needsDiscard && (
            <Alert severity="warning">
              This service already has cost prices for its current costing. They can&apos;t be carried over, so changing
              it removes them.
              <FormControlLabel
                sx={{ display: 'flex', mt: 1 }}
                control={
                  <Checkbox checked={confirmDiscard} onChange={(event) => setConfirmDiscard(event.target.checked)} />
                }
                label="Remove the existing cost prices"
              />
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isChanged || (needsDiscard && !confirmDiscard) || updateCosting.isPending}
        >
          Save
        </Button>
        <Button variant="text" onClick={onClose} disabled={updateCosting.isPending}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
